import { p256 } from '@noble/curves/nist.js';
import { sha256 } from '@noble/hashes/sha2.js';

type Point = ReturnType<typeof p256.Point.fromAffine>;

// Fixed P-256 curve points from schollz/pake v3 (role 0 = U, role 1 = V)
const U = p256.Point.fromAffine({
  x: 793136080485469241208656611513609866400481671852n,
  y: 59748757929350367369315811184980635230185250460108398961713395032485227207304n,
});

const V = p256.Point.fromAffine({
  x: 1086685267857089638167386722555472967068468061489n,
  y: 9157340230202296554417312816309453883742349874205386245733062928888341584123n,
});

interface PakeWire {
  role: number;
  ux: string;
  uy: string;
  vx: string;
  vy: string;
  xx: string;
  xy: string;
  yx: string;
  yy: string;
}

// Convert bytes to a BigInt scalar (big-endian)
function bytesToScalar(bytes: Uint8Array): bigint {
  let result = 0n;
  for (const byte of bytes) {
    result = (result << 8n) | BigInt(byte);
  }
  return result;
}

// Convert BigInt to big-endian bytes (variable length, matching Go big.Int.Bytes())
function bigintToBytes(value: bigint): Uint8Array {
  if (value === 0n) return new Uint8Array(0);
  const hex = value.toString(16);
  const padded = hex.length % 2 ? `0${hex}` : hex;
  const bytes = new Uint8Array(padded.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Number.parseInt(padded.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

export class PakeClient {
  private pw: Uint8Array;
  private alpha: Uint8Array;
  private X: { x: bigint; y: bigint };
  private Vpw: Point;
  private K: Uint8Array | null = null;

  constructor(pin: string, randomBytes?: Uint8Array) {
    this.pw = new TextEncoder().encode(pin);
    const pwScalar = bytesToScalar(this.pw);

    // Compute pw * U (used for X) and pw * V (used later in update for Z)
    const UpwPoint = U.multiply(pwScalar);
    this.Vpw = V.multiply(pwScalar);

    // Generate random scalar alpha (valid for P-256 curve order)
    if (randomBytes) {
      this.alpha = new Uint8Array(randomBytes);
    } else {
      this.alpha = p256.utils.randomSecretKey();
    }

    // Compute X = pw*U + alpha*G
    const alphaG = p256.Point.BASE.multiply(bytesToScalar(this.alpha));
    const XPoint = UpwPoint.add(alphaG);
    this.X = XPoint.toAffine();
  }

  bytes(): Uint8Array {
    const Ua = U.toAffine();
    const Va = V.toAffine();
    const wire: PakeWire = {
      role: 0,
      ux: Ua.x.toString(),
      uy: Ua.y.toString(),
      vx: Va.x.toString(),
      vy: Va.y.toString(),
      xx: this.X.x.toString(),
      xy: this.X.y.toString(),
      yx: '0',
      yy: '0',
    };
    return new TextEncoder().encode(JSON.stringify(wire));
  }

  update(serverBytes: Uint8Array): void {
    const serverText = new TextDecoder().decode(serverBytes);
    const q = JSON.parse(serverText) as PakeWire;

    if (q.role !== 1) {
      throw new Error('Expected server role 1');
    }

    if (!q.yx || q.yx === '0' || !q.yy || q.yy === '0') {
      throw new Error('Server PAKE message missing Y values');
    }

    const Y = { x: BigInt(q.yx), y: BigInt(q.yy) };

    // Verify Y is on curve
    const YPoint = p256.Point.fromAffine(Y);

    // Compute Z = (Y - pw*V) * alpha
    const diff = YPoint.add(this.Vpw.negate());
    const ZPoint = diff.multiply(bytesToScalar(this.alpha));
    const Z = ZPoint.toAffine();

    // Zero secret scalar after use
    this.alpha.fill(0);

    // Compute K = SHA256(pw || X.x || X.y || Y.x || Y.y || Z.x || Z.y)
    const parts = [
      this.pw,
      bigintToBytes(this.X.x),
      bigintToBytes(this.X.y),
      bigintToBytes(Y.x),
      bigintToBytes(Y.y),
      bigintToBytes(Z.x),
      bigintToBytes(Z.y),
    ];
    const total = parts.reduce((sum, p) => sum + p.length, 0);
    const buf = new Uint8Array(total);
    let offset = 0;
    for (const part of parts) {
      buf.set(part, offset);
      offset += part.length;
    }
    this.K = sha256(buf);
  }

  sessionKey(): Uint8Array {
    if (!this.K) {
      throw new Error('Session key not yet derived — call update() first');
    }
    return this.K;
  }
}
